import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ROUTES } from "@/routes/path"
import { ErrorCard } from "@/components/ui/error"
import { useToast } from "@/hooks/useToast"
import { useAuth, useResetPasswordChange } from "@/hooks/useAuth"

const newPasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(8, "Password must be at least 8 characters"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  })

type NewPasswordFormValues = z.infer<typeof newPasswordSchema>

export default function ResetPasswordNew() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [passwordChanged, setPasswordChanged] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { email } = useAuth()

  const mutation = useResetPasswordChange({
    onSuccess: () => {
      setPasswordChanged(true)
      localStorage.removeItem("resetToken")
      toast({
        title: "Password changed successfully",
        description: "Your password has been updated. You can now sign in with your new password.",
        variant: "success",
      })
    },
    onError: (error: any) => {
      setFormError(error?.response?.data?.error || "Failed to reset password")
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
  } = useForm<NewPasswordFormValues>({
    resolver: zodResolver(newPasswordSchema),
  })

  if (passwordChanged) {
    return (
      <div className="relative flex h-screen flex-col items-center bg-gray-50 dark:bg-zinc-900">
        <div className="relative z-10 flex w-full flex-col items-center">
          <div className="my-10 flex justify-center">
            <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
          </div>
          {/* Success Card */}
          <div className="mx-2 max-h-fit w-full max-w-lg rounded-xl bg-white p-8 shadow-xl dark:bg-zinc-800">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                <svg
                  className="h-6 w-6 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="mb-4 text-2xl font-semibold dark:text-zinc-100">Password changed successfully</h1>
              <p className="mb-6 text-gray-600 dark:text-gray-400">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <Button onClick={() => navigate(ROUTES.AUTH.LOGIN)} className="w-full">
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative flex h-screen flex-col items-center bg-gray-50 dark:bg-zinc-900">
      <div className="relative z-10 flex w-full flex-col items-center">
        <div className="my-10 flex justify-center">
          <img src="/logo.svg" alt="Ledgerbeam" width={220} height={50} />
        </div>
        {/* Card */}
        <div className="mx-2 max-h-fit w-full max-w-lg rounded-xl bg-white p-8 shadow-xl dark:bg-zinc-800">
          <h1 className="mb-8 text-2xl font-semibold dark:text-zinc-100">Create new password</h1>
          <p className="mb-6 text-gray-600 dark:text-gray-400">
            Enter your new password below. Make sure it&apos;s at least 8 characters long.
          </p>
          <form
            onSubmit={handleSubmit((data: NewPasswordFormValues) => {
              const token = localStorage.getItem("resetToken") || ""
              mutation.mutate({ email: email || "", token, newPassword: data.password })
            })}
            className="space-y-6"
          >
            <div className="grid gap-4">
              <Input
                id="password"
                placeholder="Enter your new password"
                type={showPassword ? "text" : "password"}
                label="New Password"
                disabled={mutation.isPending}
                {...register("password")}
                error={errors.password?.message}
                touched={touchedFields.password}
                suffix={
                  <button
                    type="button"
                    tabIndex={-1}
                    className="focus:outline-none"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-5 text-gray-400 hover:text-gray-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-5 text-gray-400 hover:text-gray-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                        />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                }
              />
            </div>

            <div className="grid gap-4">
              <Input
                id="confirmPassword"
                placeholder="Confirm your new password"
                type={showConfirmPassword ? "text" : "password"}
                label="Confirm New Password"
                disabled={mutation.isPending}
                {...register("confirmPassword")}
                error={errors.confirmPassword?.message}
                touched={touchedFields.confirmPassword}
                suffix={
                  <button
                    type="button"
                    tabIndex={-1}
                    className="focus:outline-none"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-5 text-gray-400 hover:text-gray-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-5 text-gray-400 hover:text-gray-500"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                        />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                }
              />
            </div>
            {formError && <ErrorCard message={formError} />}
            <Button type="submit" disabled={mutation.isPending} className="w-full">
              {mutation.isPending ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </div>

        <div className="-mt-4 w-full max-w-lg rounded-b-xl border-t bg-gray-50 px-8 py-4 text-center text-sm text-muted-foreground shadow-md shadow-gray-200 dark:border-zinc-700 dark:bg-zinc-800 dark:shadow-zinc-700">
          Remember your password?{" "}
          <Link to={ROUTES.AUTH.LOGIN} className="font-medium text-primary hover:underline dark:text-primary">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
